"use client";

import React, { useState, type FC } from "react";
import styles from "./index.module.scss";

import { IoIosSearch } from "react-icons/io";
import classNames from "classnames";
import { Input } from "antd";
import type { HexString } from "@/types/general.types";
import { useParams, useRouter } from "next/navigation";
import { useBridgeScannerStore } from "@/features/bridge-scanner/stores/bridge-scanner.store";
import { useQueryClient } from "@tanstack/react-query";
import { getTransaction } from "@/features/bridge-scanner/api/get-transaction";
import { AnimatePresence, motion } from "framer-motion";
import LoadingIcon from "@/components/layout/loading-icon";
import { useMainnetToTestnetSwitchStore } from "@/stores/mainet-testnet-switcher.store";

interface Props {
  txHash?: HexString;
}

const BridgeScannerSearch: FC<Props> = ({ txHash }) => {
  const { network } = useParams() as { network: string };
  const queryClient = useQueryClient();
  const [isFocused, setFocused] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const isMainnet = useMainnetToTestnetSwitchStore((store) => store.isMainnet);

  const router = useRouter();
  const { address, setAddress } = useBridgeScannerStore();

  const checkHashType = (
    hash: HexString | string,
  ): "tx" | "address" | undefined => {
    if (hash.length === 66) return "tx";
    else if (hash.length === 42) return "address";
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError("");
    setAddress(e.target.value);
  };

  const handleSubmit = async () => {
    // Take input value, lowercase it, call the getTransactionByHash function with the input value as the argument
    if (address.length <= 0) {
      return;
    }
    const hashType = checkHashType(address);
    if (hashType === "tx") {
      queryAndNavigate();
    } else if (hashType === "address") {
      console.log("Address");
      const params = new URLSearchParams();
      params.set("address", address);
      router.push(`?${params.toString()}`, { scroll: false });
    }
  };

  const queryAndNavigate = async () => {
    setIsLoading(true);
    const data = await queryClient.fetchQuery({
      queryKey: ["tx_overview", address, isMainnet],
      queryFn: () =>
        getTransaction({
          hash: address as HexString,
          isMainnet: false,
        }),
    });
    setIsLoading(false);
    if (data && Object.keys(data).length > 0) {
      router.push(`/bridge-scanner/${network}/tx/${address}`);
    } else {
      setError("That transaction doesn’t appear to exist.");
    }
  };

  return (
    <div className={styles.searchContainer}>
      <div className={styles.loader}>
        {isLoading ? (
          <LoadingIcon size={25} />
        ) : (
          <IoIosSearch
            size={24}
            className={classNames(styles.icon, isFocused && styles.focused)}
          />
        )}
      </div>

      <Input
        type="text"
        maxLength={66}
        onFocus={() => setFocused(true)}
        onChange={handleInputChange}
        onBlur={() => setFocused(false)}
        value={address}
        placeholder={txHash || "Search by transaction hash or address"}
        onPressEnter={handleSubmit}
      />

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ translateX: -5, opacity: 0 }}
            animate={{ translateX: 0, opacity: 1 }}
            className={styles.error}
          >
            {error}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BridgeScannerSearch;
